import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X, AudioLines, LogOut, LayoutDashboard } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/team", label: "Our Team" },
  { to: "/products", label: "Products" },
  { to: "/remedies", label: "Remedies" },
  { to: "/meditations", label: "Meditations" },
  { to: "/workshops", label: "Workshops" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/book", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-primary/15 text-primary group-hover:bg-primary/25 transition-colors">
            <AudioLines size={20} strokeWidth={1.5} />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-lg text-foreground tracking-tight">VoiceMatrix AI</span>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Voice-frequency wellness</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm transition-colors ${
                  isActive ? "text-primary" : "text-secondary-foreground hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          {user && typeof user === "object" ? (
            <>
              {user.role === "admin" && (
                <Button variant="ghost" className="rounded-full text-secondary-foreground hover:text-primary" onClick={() => navigate("/admin")} data-testid="nav-admin-btn">
                  Admin
                </Button>
              )}
              <Button variant="ghost" className="rounded-full text-secondary-foreground hover:text-primary" onClick={() => navigate("/app")} data-testid="nav-dashboard-btn">
                <LayoutDashboard size={16} strokeWidth={1.5} /> Dashboard
              </Button>
              <Button variant="outline" className="rounded-full border-border" onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-btn">
                <LogOut size={16} strokeWidth={1.5} /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="rounded-full text-secondary-foreground hover:text-primary" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                Log in
              </Button>
              <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" onClick={() => navigate("/register")} data-testid="nav-signup-btn">
                Free Scan
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden text-foreground" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden glass border-t border-white/5 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)}
              className="px-3 py-2.5 rounded-lg text-secondary-foreground hover:text-primary hover:bg-white/5 transition-colors">
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-2 pt-2">
            {user && typeof user === "object" ? (
              <Button className="flex-1 rounded-full bg-primary text-primary-foreground" onClick={() => { setOpen(false); navigate("/app"); }}>Dashboard</Button>
            ) : (
              <>
                <Button variant="outline" className="flex-1 rounded-full border-border" onClick={() => { setOpen(false); navigate("/login"); }}>Log in</Button>
                <Button className="flex-1 rounded-full bg-primary text-primary-foreground" onClick={() => { setOpen(false); navigate("/register"); }}>Free Scan</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
